/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { ComponentChildren, FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ActionButton/ActionButton.css';

export interface ActionButtonProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'icon'> {
  children?: ComponentChildren;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  active?: boolean;
  disabled?: boolean;
}

export const ActionButton: FunctionComponent<ActionButtonProps> = ({
  icon,
  className,
  children,
  active = false,
  disabled = false,
  ...props
}) => {
  return (
    <button
      role="button"
      disabled={disabled}
      {...props}
      className={classes([
        'dropin-action-button',
        ['dropin-action-button--active', active],
        ['dropin-action-button--disabled', disabled],
        className,
      ])}
    >
      {icon && (
        <VComponent
          node={icon}
          className={classes(['dropin-action-button-icon'])}
        />
      )}
      {children &&
        (typeof children === 'string' ? <span>{children}</span> : children)}
    </button>
  );
};
